<template>
    <section class="booking-form-card" aria-labelledby="booking-visit-title">
        <div class="booking-section-heading">
            <span class="booking-section-icon"><feather type="calendar" aria-hidden="true" /></span>
            <div>
                <h3 id="booking-visit-title">{{ $t("reservationForm.visit") }}</h3>
                <p>{{ $t("reservationForm.visitHint") }}</p>
            </div>
        </div>
        <div class="booking-visit-inputs">
            <div>
                <label for="booking-date">{{ $t("reservationForm.date") }}</label>
                <input id="booking-date" type="date" class="form-control" required :value="dateValue" @change="changeDate($event.target.value)" />
                <div class="booking-quick-dates">
                    <button
                        v-for="offset in [0, 1]"
                        :key="offset"
                        type="button"
                        @click="changeDate(getDateTime().plus({ days: offset }).toISODate())">
                        {{ $tl(offset ? "labels.tomorrow" : "labels.today") }}
                    </button>
                </div>
            </div>
            <div v-for="field in ['nb_pers', 'nb_children']" :key="field">
                <label :for="`booking-${field}`">{{ $t(field === "nb_pers" ? "reservationForm.adults" : "reservationForm.children") }}</label>
                <div class="booking-counter">
                    <button
                        type="button"
                        :aria-label="`${$t(field === 'nb_pers' ? 'reservationForm.adults' : 'reservationForm.children')} −1`"
                        :disabled="reservation[field] <= (field === 'nb_pers' ? 1 : 0)"
                        @click="setCount(field, -1)">
                        −
                    </button>
                    <input
                        :id="`booking-${field}`"
                        type="number"
                        inputmode="numeric"
                        step="1"
                        :min="field === 'nb_pers' ? 1 : 0"
                        required
                        v-model.number="reservation[field]"
                        :aria-invalid="Boolean(errors[field])" />
                    <button
                        type="button"
                        :aria-label="`${$t(field === 'nb_pers' ? 'reservationForm.adults' : 'reservationForm.children')} +1`"
                        @click="setCount(field, 1)">
                        +
                    </button>
                </div>
                <small v-if="errors[field]" class="text-danger">{{ errors[field][0] }}</small>
            </div>
        </div>
        <div class="booking-pax-shortcuts" :aria-label="$t('reservationForm.adults')">
            <button
                v-for="count in [1, 2, 3, 4, 5, 6, 8]"
                :key="count"
                type="button"
                :aria-pressed="reservation.nb_pers === count ? 'true' : 'false'"
                :class="{ selected: reservation.nb_pers === count }"
                @click="reservation.nb_pers = count">
                {{ count }}
            </button>
        </div>
        <div class="booking-slot-heading">
            <h4>{{ $t("reservationForm.schedule") }}</h4>
            <small>{{ $t("reservationForm.scheduleHint") }}</small>
        </div>
        <p v-if="loading" class="booking-inline-status" role="status"><span class="booking-spinner" />{{ $t("reservationForm.loadingSlots") }}</p>
        <div v-else-if="error" class="booking-alert" role="alert">
            {{ error }} <button type="button" class="booking-text-button" @click="$emit('retry')">{{ $t("reservationForm.retry") }}</button>
        </div>
        <p v-else-if="!services.length" class="booking-empty" role="status">{{ $t("reservationForm.noSlots") }}</p>
        <div v-else class="booking-services">
            <div v-for="service in services" :key="service.id" class="booking-service">
                <button
                    type="button"
                    class="booking-service-heading booking-service-toggle"
                    :aria-expanded="expandedServiceId === service.id ? 'true' : 'false'"
                    :aria-controls="`booking-service-${service.id}`"
                    @click="activeServiceId = expandedServiceId === service.id ? null : service.id">
                    <strong>{{ service.name }}</strong>
                    <span
                        >{{ service.pax || 0
                        }}<template v-if="service.new_pax || service.max_pax"> / {{ service.new_pax ?? service.max_pax }}</template>
                        {{ $tl("labels.personsShort") }}</span
                    >
                    <span v-if="service.is_full === 1" class="booking-full-badge">{{ $tl("labels.booking.services.full") }}</span>
                    <feather :type="expandedServiceId === service.id ? 'chevron-up' : 'chevron-down'" aria-hidden="true" />
                </button>
                <div v-show="expandedServiceId === service.id" :id="`booking-service-${service.id}`" class="booking-slots">
                    <button
                        v-for="slot in service.slotsWithPax?.data ?? []"
                        :key="slot.id"
                        type="button"
                        class="booking-slot"
                        :class="{ selected: slot.id === reservation.slot_id }"
                        :aria-pressed="slot.id === reservation.slot_id ? 'true' : 'false'"
                        @click="$emit('select-slot', { slot, service })">
                        <strong>{{ slot.hour_start }}</strong>
                        <span>{{ service.is_table_rotation_enabled ? slot.pax : slot.pax_real }} {{ $tl("labels.personsShort") }}</span>
                    </button>
                </div>
            </div>
        </div>
        <small v-if="errors.slot_id" class="text-danger" role="alert">{{ errors.slot_id[0] }}</small>
        <details v-if="restaurants.length > 1" class="booking-restaurant-switch">
            <summary class="booking-text-button"><feather type="repeat" aria-hidden="true" />{{ $t("reservationForm.changeRestaurant") }}</summary>
            <label for="booking-restaurant">{{ $tl("labels.form.restaurant.select") }}</label>
            <select
                id="booking-restaurant"
                class="form-select"
                :value="restaurantId"
                @change="
                    $emit(
                        'change-restaurant',
                        restaurants.find((restaurant) => Number(restaurant.id) === Number($event.target.value))
                    )
                ">
                <option v-for="restaurant in restaurants" :key="restaurant.id" :value="restaurant.id">{{ restaurant.name }}</option>
            </select>
        </details>
    </section>
</template>

<script>
export default {
    props: {
        reservation: { type: Object, required: true },
        services: { type: Array, default: () => [] },
        loading: Boolean,
        error: { default: null },
        errors: { type: Object, default: () => ({}) },
        restaurantId: { required: true },
        restaurants: { type: Array, default: () => [] },
        preferredServiceId: { default: null },
    },
    emits: ["select-slot", "change-date", "retry", "change-restaurant"],
    data() {
        return { activeServiceId: this.preferredServiceId ?? undefined };
    },
    computed: {
        expandedServiceId() {
            if (this.activeServiceId === null) return null;
            return this.services.find((service) => Number(service.id) === Number(this.activeServiceId))?.id || this.services[0]?.id;
        },
        dateValue() {
            return this.reservation.reservation_date?.toISODate() ?? "";
        },
    },
    watch: {
        "reservation.service.id"(id) {
            if (id) this.activeServiceId = id;
        },
    },
    methods: {
        changeDate(value) {
            if (value && value !== this.dateValue) this.$emit("change-date", this.getDateTime(value, false));
        },
        setCount(field, delta) {
            this.reservation[field] = Math.max(field === "nb_pers" ? 1 : 0, Number(this.reservation[field] || 0) + delta);
        },
    },
};
</script>
